import { createSignal, For, Show, type Component } from 'solid-js';

import type { GameStore } from '../state/store';
import {
  activeEffects,
  countdownText,
  effectDetail,
  EFFECT_HUE,
  EFFECT_LETTER,
  EFFECT_NAME,
  sweepFraction,
  type EffectKind,
} from './statusEffects';

/**
 * The player's own active effects, as a row of icons above the action bar. Phase 32.6, from
 * `doc/grind/design/STATUS-EFFECT-DISPLAY.md`.
 *
 * **Above the bar rather than in it.** The bar is seven slots plus the belt plus the stamina bar
 * plus two orbs and is already the densest thing on screen; an effect is *state* and the bar is an
 * input surface, and mixing the two invites a click on a debuff.
 *
 * **The player's effects only.** A row that also showed the target's would be two lists that look
 * alike and mean different things. What is on a *monster* is said by tinting the monster — see
 * `EntityRegistry.setStatusTint` — which costs no HUD space at all.
 *
 * **Placeholder letters, on purpose.** The icon sheet is five cells — four CC kinds plus the Mana
 * Shield stance — and one `slice-icons.py` run away; until it exists a box with a letter says
 * *"placeholder"* to anyone looking, where a borrowed skill icon would say *"this is the art"*.
 *
 * **Two frames, one row.** A buff and a debuff share the slot geometry and are told apart by the
 * rim colour, which is §3's decision: a second row would be empty half the time. A buff also draws
 * no sweep and no countdown, because a stance has no duration — see `ActiveEffect.seconds`.
 *
 * ⚠ **jsdom cannot verify this component's actual job.** Whether the row is on screen, whether it
 * sits clear of the bar, and whether the hover card lands somewhere readable are all layout, and
 * jsdom computes none. `scripts/browser/` and `elementFromPoint` are the check that cannot be
 * fooled — AGENTS §5.
 */
export const StatusEffectRow: Component<{ store: GameStore }> = (props) => {
  const pools = () => props.store.character()?.pools;
  const effects = () => activeEffects(props.store.self(), pools());

  /** Which icon the pointer is resting on, for the card. Null when it is on none. */
  const [hovered, setHovered] = createSignal<EffectKind | null>(null);

  return (
    <Show when={effects().length > 0}>
      <div class="effects" data-testid="status-effects">
        <For each={effects()}>
          {(effect) => (
            <div
              class="effects__icon hud-interactive"
              data-testid={`status-effect-${effect.kind}`}
              data-kind={effect.kind}
              // The frame's polarity, which is how §3 tells a buff from a debuff without a second
              // row: same slot geometry, different rim.
              data-polarity={effect.polarity}
              style={{
                // The hue drives the frame and the sweep together, so one number is the whole of an
                // effect's identity on screen.
                '--effect-hue': `${EFFECT_HUE[effect.kind]}`,
                // An indefinite effect draws no sweep at all rather than a full one: a stance
                // rendered as a permanently-brimming timer says "about to run out" forever.
                '--effect-sweep': effect.seconds === null ? '0' : `${sweepFraction(effect.seconds)}`,
              }}
              onPointerEnter={() => setHovered(effect.kind)}
              onPointerLeave={() => setHovered((k) => (k === effect.kind ? null : k))}
            >
              <span class="effects__glyph" aria-hidden="true">
                {EFFECT_LETTER[effect.kind]}
              </span>
              <Show when={effect.seconds !== null && countdownText(effect.seconds)}>
                {(text) => <span class="effects__count">{text()}</span>}
              </Show>
              {/* The name is the accessible label, since the glyph is a placeholder letter. */}
              <span class="effects__label">{EFFECT_NAME[effect.kind]}</span>

              <Show when={hovered() === effect.kind}>
                <div class="effects__card plate" role="tooltip" data-testid="status-effect-card">
                  <div class="effects__card-name">{EFFECT_NAME[effect.kind]}</div>
                  <div class="effects__card-body">
                    {effectDetail(effect.kind, props.store.self(), pools())}
                  </div>
                  {/*
                    No time line on a stance. "Until you turn it off" is what the body already
                    says, and a duration row reading `0.0s left` would contradict it.
                  */}
                  <Show when={effect.seconds !== null}>
                    <div class="effects__card-time">{effect.seconds!.toFixed(1)}s left</div>
                  </Show>
                  {/*
                    **No "applied by" line, and its absence is deliberate.** `CCState` keeps one
                    expiry per kind and no provenance (ADR 0007), so the server genuinely cannot say
                    which skill or monster put this here. A guessed attribution would be the worst
                    kind of tooltip: confidently wrong. The spec's §7 records where it would go.
                  */}
                </div>
              </Show>
            </div>
          )}
        </For>
      </div>
    </Show>
  );
};
